// スキルカテゴリーと固定スキル項目の定義

export type SkillRating = 1 | 2 | 3 | 4 | 5 | 6 // 1:E, 2:D, 3:C, 4:B, 5:A, 6:S

export type SkillCategory = 'engineering' | 'game' | 'creative' | 'language'

// Career__cのフィールド名とスキル項目名のマッピング
export interface SkillFieldMapping {
  label: string // 表示用ラベル（【...スキル】を除いた部分）
  careerField: string // Career__cのフィールド名
  category: SkillCategory
  tooltips?: string[] // レイティングのツールチップ配列（6段階分、UIUXと映像スキルのみ）
}

// レイティング値の変換（UI 1-6 → Salesforce S-E）
export const ratingToSalesforce = (rating: SkillRating): string => {
  const map: Record<SkillRating, string> = {
    6: 'S',
    5: 'A',
    4: 'B',
    3: 'C',
    2: 'D',
    1: 'E',
  }
  return map[rating]
}

// レイティング値の変換（Salesforce S-E → UI 1-6）
export const salesforceToRating = (value: string | null | undefined): SkillRating | null => {
  if (!value) return null

  // 説明文付きの場合（例：'A:ユーザーの本質的な...'）、先頭の文字を取得
  const letter = value.charAt(0).toUpperCase()

  const map: Record<string, SkillRating> = {
    S: 6,
    A: 5,
    B: 4,
    C: 3,
    D: 2,
    E: 1,
  }
  return map[letter] || null
}

// UIUXスキルのツールチップ（6段階: [0]=E, [1]=D, ..., [5]=S）
const UIUX_TOOLTIPS = [
  'E: 経験なし',
  'D: 自分のアイデア・考えた課題をもとに、デザインを実装できる。',
  'C: ユーザーの本質的な課題を理解し、ユーザーが求めるデザインを実装できる。',
  'B: ユーザーの本質的な課題を理解し、エンジニアが開発した機能を使いやすいデザインとして実装できる（チームと協働できる）。',
  'A: ユーザーの本質的な課題を理解し、課題を解決するために必要な機能を提案し、ユーザーが求めるデザインとして実装できる。',
  'S: ユーザーの本質的な課題を理解し、戦略レイヤーから思考した上で、ユーザーが求めるデザインとして実装できる。',
]

// 映像制作スキルのツールチップ（6段階）
const VIDEO_TOOLTIPS = [
  'E: 経験なし',
  'D: 授業レベルの簡単な撮影・編集を経験している。',
  'C: Premiereなどの主要ソフトを使って短い映像作品を作れる。基本的な撮影・編集スキルを有している。',
  'B: モーショングラフィックスやカラーコレクションなど一歩踏み込んだ編集もでき、そこそこの完成度の動画を制作可能。',
  'A: チーム開発経験や映像全体のディレクション・企画力があり、より高度な合成や色補正、照明・カメラワークなど映像制作の本格知識を活用できる。',
  'S: 映像制作の先端技術を使いこなし、制作における全工程をリードできる。技術的・芸術的背景を踏まえた総合ディレクションが可能。',
]

// 言語スキルのツールチップ（6段階）
export const LANGUAGE_TOOLTIPS = [
  'E: 学習経験なし',
  'D: 基礎的な単語や簡単な文章を理解できる',
  'C: 日常会話や基本的な読み書きができる',
  'B: 業務で使用でき、複雑な内容も理解・表現できる',
  'A: ビジネスレベルで流暢に使いこなせる',
  'S: ネイティブレベル、または専門的な内容も高度に扱える',
]

// 固定スキル項目の定義
export const FIXED_SKILLS: SkillFieldMapping[] = [
  // エンジニアリングスキル
  { label: 'C', careerField: 'DS_C__c', category: 'engineering' },
  { label: 'DTM経験', careerField: 'DS_DTM__c', category: 'engineering' },
  { label: 'C++', careerField: 'DS_ccc__c', category: 'engineering' },
  { label: 'Swift', careerField: 'DS_Swift__c', category: 'engineering' },
  { label: 'PHP', careerField: 'DS_PHP__c', category: 'engineering' },
  { label: 'Java', careerField: 'DS_Java__c', category: 'engineering' },
  { label: 'Ruby', careerField: 'DS_Ruby__c', category: 'engineering' },
  { label: 'Python', careerField: 'DS_Python__c', category: 'engineering' },
  { label: 'SQL', careerField: 'DS_SQL__c', category: 'engineering' },
  { label: 'HTML・CSS', careerField: 'HTMLCSS__c', category: 'engineering' },
  { label: 'Dart', careerField: 'ds_dart__c', category: 'engineering' },
  { label: 'Go', careerField: 'DS_Go__c', category: 'engineering' },
  { label: 'JavaScript', careerField: 'DS_JavaScript__c', category: 'engineering' },
  { label: 'Rust', careerField: 'DS_Rust__c', category: 'engineering' },
  { label: 'Typescript', careerField: 'DS_Typescript__c', category: 'engineering' },
  { label: 'Scala', careerField: 'DS_Scala__c', category: 'engineering' },
  { label: 'シェーディング言語/GSLS、HLSL、ShaderLabなど', careerField: 'DS_GSLS__c', category: 'engineering' },
  { label: 'Kotlin', careerField: 'DS_Kotlin__c', category: 'engineering' },
  { label: 'p5.js', careerField: 'ds_p5_js__c', category: 'engineering' },
  { label: 'Objective-C', careerField: 'DS_ObC__c', category: 'engineering' },

  // ゲーム開発スキル
  { label: 'Unreal Engine', careerField: 'ds_unrealengine__c', category: 'game' },
  { label: 'Unity', careerField: 'DS_Unity__c', category: 'game' },
  { label: 'C#', careerField: 'DS_C_sharp__c', category: 'game' },

  // クリエイティブスキル
  { label: 'UIUXの経験', careerField: 'design_skill__c', category: 'creative', tooltips: UIUX_TOOLTIPS },
  { label: '映像制作経験', careerField: 'videoproduction_skill__c', category: 'creative', tooltips: VIDEO_TOOLTIPS },
]

// カテゴリー名の表示用ラベル
export const CATEGORY_LABELS: Record<SkillCategory, string> = {
  engineering: 'エンジニアリングスキル',
  game: 'ゲーム開発スキル',
  creative: 'クリエイティブスキル',
  language: '言語スキル',
}

// カテゴリー別にスキルを取得
export const getSkillsByCategory = (category: SkillCategory): SkillFieldMapping[] => {
  return FIXED_SKILLS.filter((skill) => skill.category === category)
}
